<template>
  <el-form-item label="参会人">
    <div class="participant-bar">
      <el-select v-model="participantType" size="small" class="participant-bar__type" @change="onParticipantTypeChange">
        <el-option v-for="item in participantTypeOptions" :key="item.value"
          :label="item.label" :value="item.value"></el-option>
      </el-select>
      <el-button size="small" type="primary" plain icon="el-icon-plus"
        :disabled="!canOpenPicker" @click="openPickerDialog">
        添加{{ participantTypeLabel }}
      </el-button>
    </div>

    <div v-if="selections.length" class="participant-selection">
      <div class="participant-selection__bar">
        <span class="participant-selection__summary">{{ selectionSummaryText }}</span>
        <div class="participant-selection__bar-actions">
          <el-button v-if="showManageButton" type="text" size="mini" @click="listDialogVisible = true">查看全部</el-button>
          <el-button type="text" size="mini" @click="clearAllParticipants">清空</el-button>
        </div>
      </div>
      <div :class="['participant-tags', { 'is-scroll': selections.length > tagInlineMax }]">
        <el-tag v-for="(p, index) in visibleTagSelections" :key="p.Type + '-' + p.Id"
          size="small" closable @close="removeParticipant(getSelectionIndex(p))">
          {{ participantTypeText(p.Type) }}：{{ p.Name }}
        </el-tag>
        <el-tag v-if="hiddenTagCount > 0" size="small" type="info" class="participant-tags__more"
          @click="listDialogVisible = true">+{{ hiddenTagCount }}</el-tag>
      </div>
    </div>
    <div v-else class="field-tip mr-field-tip">
      {{ fieldTip }}
    </div>

    <el-dialog :title="pickerDialogTitle" :visible.sync="pickerDialogVisible" width="520px"
      append-to-body :close-on-click-modal="false" custom-class="mr-participant-dialog" destroy-on-close
      @open="onPickerDialogOpen" @closed="onPickerDialogClosed">
      <!-- 学生：先选班级，再勾选学生 -->
      <template v-if="participantType === 2">
        <div class="picker-dialog__scope-tip" :class="{ 'is-muted': !effectiveCampusId }">
          <i class="el-icon-location-outline"></i>
          <span v-if="effectiveCampusId">按会议室校区：{{ campusDisplayName }}</span>
          <span v-else>当前会议室未绑定校区，显示全校班级</span>
        </div>
        <div class="picker-dialog__block">
          <div class="picker-dialog__label">选择班级</div>
          <el-cascader v-model="studentClassId" :options="classTreeOptions"
            :props="classCascaderProps" filterable clearable
            :placeholder="effectiveCampusId ? '请选择本校区班级' : '请选择班级'" style="width: 100%;"
            :loading="deptTreeLoading" :disabled="!schoolId || !classTreeOptions.length"
            @change="onStudentClassChange" />
        </div>
        <div class="picker-dialog__block">
          <div class="picker-dialog__label-row">
            <span class="picker-dialog__label">选择学生</span>
            <span v-if="studentClassId && filteredDialogOptions.length" class="picker-dialog__actions">
              <el-button type="text" size="mini" @click="selectAllDialogOptions">全选</el-button>
              <el-button type="text" size="mini" @click="dialogPickKeys = []">清空</el-button>
            </span>
          </div>
          <el-input v-model="dialogSearch" size="small" clearable prefix-icon="el-icon-search"
            placeholder="搜索姓名" class="picker-dialog__search" :disabled="!studentClassId" />
          <div v-loading="participantLoading" class="picker-dialog__list">
            <template v-if="!studentClassId">
              <div class="picker-dialog__empty">请先选择班级</div>
            </template>
            <template v-else-if="!filteredDialogOptions.length">
              <div class="picker-dialog__empty">{{ dialogSearch ? '未找到匹配学生' : '该班暂无学生' }}</div>
            </template>
            <el-checkbox-group v-else v-model="dialogPickKeys" class="picker-dialog__checks">
              <el-checkbox v-for="p in filteredDialogOptions" :key="optionKey(p)" :label="optionKey(p)">
                {{ participantOptionLabel(p) }}
              </el-checkbox>
            </el-checkbox-group>
          </div>
        </div>
      </template>

      <!-- 教师：按会议室校区筛选 -->
      <template v-else-if="participantType === 1">
        <div class="picker-dialog__scope-tip" :class="{ 'is-muted': !effectiveCampusId }">
          <i class="el-icon-location-outline"></i>
          <span v-if="effectiveCampusId">按会议室校区：{{ campusDisplayName }}</span>
          <span v-else>当前会议室未绑定校区，显示全校教师</span>
        </div>
        <div class="picker-dialog__block">
          <div class="picker-dialog__label-row">
            <span class="picker-dialog__label">选择教师</span>
            <span v-if="filteredDialogOptions.length" class="picker-dialog__actions">
              <el-button type="text" size="mini" @click="selectAllDialogOptions">全选</el-button>
              <el-button type="text" size="mini" @click="dialogPickKeys = []">清空</el-button>
            </span>
          </div>
          <el-input v-model="dialogSearch" size="small" clearable prefix-icon="el-icon-search"
            placeholder="搜索教师姓名" class="picker-dialog__search" />
          <div v-loading="participantLoading" class="picker-dialog__list">
            <template v-if="!filteredDialogOptions.length">
              <div class="picker-dialog__empty">{{ dialogSearch ? '未找到匹配教师' : '该校区暂无教师' }}</div>
            </template>
            <el-checkbox-group v-else v-model="dialogPickKeys" class="picker-dialog__checks">
              <el-checkbox v-for="p in filteredDialogOptions" :key="optionKey(p)" :label="optionKey(p)">
                {{ participantOptionLabel(p) }}
              </el-checkbox>
            </el-checkbox-group>
          </div>
        </div>
      </template>

      <!-- 部门：树形多选，任意节点 -->
      <template v-else-if="participantType === 4">
        <div class="picker-dialog__scope-tip" :class="{ 'is-muted': !effectiveCampusId }">
          <i class="el-icon-location-outline"></i>
          <span v-if="effectiveCampusId">按会议室校区：{{ campusDisplayName }}</span>
          <span v-else>当前会议室未绑定校区，显示全校部门</span>
        </div>
        <div class="picker-dialog__block">
          <el-input v-model="dialogSearch" size="small" clearable prefix-icon="el-icon-search"
            placeholder="搜索部门名称" class="picker-dialog__search" @input="filterOrgTree" />
          <div v-loading="deptTreeLoading" class="picker-dialog__list picker-dialog__list--tree">
            <el-tree v-if="orgTreeOptions.length" ref="orgTree" :data="orgTreeOptions"
              show-checkbox check-strictly node-key="value"
              :props="orgTreeProps" :filter-node-method="filterOrgNode"
              :default-expanded-keys="orgTreeExpandedKeys"
              @check="onOrgTreeCheck">
              <span slot-scope="{ node }" class="org-tree-node">
                <span>{{ node.label }}</span>
              </span>
            </el-tree>
            <div v-else class="picker-dialog__empty">暂无部门数据</div>
          </div>
          <p class="picker-dialog__hint">任选部门节点，通知该节点及下属全部任教教师；选上级会自动取消下级重复项</p>
        </div>
      </template>

      <div slot="footer">
        <span v-if="participantType === 4 && orgCheckedCount" class="picker-dialog__footer-tip">
          已选 {{ orgCheckedCount }} 个部门
        </span>
        <span v-else-if="participantType !== 4 && dialogPickKeys.length" class="picker-dialog__footer-tip">
          已选 {{ dialogPickKeys.length }} 项
        </span>
        <el-button @click="pickerDialogVisible = false">取消</el-button>
        <el-button type="primary" :disabled="confirmDisabled" @click="confirmPickerDialog">
          {{ participantType === 4 ? '确定添加' : '确定添加' }}
        </el-button>
      </div>
    </el-dialog>

    <el-dialog title="参会人列表" :visible.sync="listDialogVisible" width="480px"
      append-to-body custom-class="mr-participant-list-dialog" destroy-on-close>
      <div class="participant-list-dialog__toolbar toolbar-filter-bar toolbar-filter-bar--embed">
        <el-form :inline="true" class="toolbar-filter-form" @submit.native.prevent>
          <div class="toolbar-filter-fields">
            <el-form-item>
              <el-input v-model="listSearch" clearable prefix-icon="el-icon-search"
                placeholder="姓名" class="toolbar-field-w-md" />
            </el-form-item>
            <el-form-item>
              <span class="participant-list-dialog__count">共 {{ selections.length }} 人</span>
            </el-form-item>
          </div>
        </el-form>
      </div>
      <div class="participant-list-dialog__body">
        <template v-if="filteredListSelections.length">
          <div v-for="(p, index) in filteredListSelections" :key="p.Type + '-' + p.Id + '-' + index"
            class="participant-list-dialog__item">
            <span class="participant-list-dialog__name">
              <span class="participant-list-dialog__type">{{ participantTypeText(p.Type) }}</span>
              {{ p.Name }}
            </span>
            <el-button type="text" size="mini" class="participant-list-dialog__remove"
              @click="removeParticipant(getSelectionIndex(p))">移除</el-button>
          </div>
        </template>
        <div v-else class="participant-list-dialog__empty">未找到匹配参会人</div>
      </div>
    </el-dialog>
  </el-form-item>
</template>

<script>
import { getStudentSelectListByClassId } from '../api/meetingRoomApi';
import { fetchTeachingTeachersByDeptId } from '../utils/meetingRoomTeacherCache';
import { normalizeOptionalIdString } from '../utils/meetingRoomForm';
import {
  buildCampusScopedPickerTrees,
  collectDescendantNodeIds,
  findAncestorNodeIds,
  dedupeOrgSelectionNodes,
  loadSchoolDeptTree,
  resolveOrgParticipantType
} from '../utils/meetingRoomDeptTree';
import { getDeptCascaderProps, normalizeDeptId, deptIdsEqual } from '../utils/deptCascaderProps';

const cascaderBaseProps = getDeptCascaderProps({ emitPath: false });

export default {
  name: 'MeetingRoomParticipantPicker',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    schoolId: {
      type: Number,
      default: 0
    },
    campusId: {
      type: Number,
      default: 0
    },
    campusName: {
      type: String,
      default: ''
    },
    roomId: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      participantType: 1,
      participantOptions: [],
      participantLoading: false,
      studentClassId: null,
      deptTreeLoading: false,
      schoolDeptNode: null,
      classTreeOptions: [],
      orgTreeOptions: [],
      orgTreeProps: {
        label: 'label',
        children: 'children'
      },
      orgCheckedCount: 0,
      classCascaderProps: Object.assign({}, cascaderBaseProps),
      participantTypeOptions: [
        { value: 1, label: '教师' },
        { value: 2, label: '学生' },
        { value: 4, label: '部门' }
      ],
      pickerDialogVisible: false,
      listDialogVisible: false,
      listSearch: '',
      tagInlineMax: 8,
      tagPreviewCount: 5,
      dialogPickKeys: [],
      dialogSearch: '',
      teacherLoadedKey: ''
    };
  },
  computed: {
    selections: {
      get() {
        return this.value || [];
      },
      set(list) {
        this.$emit('input', list);
      }
    },
    participantTypeLabel() {
      const map = { 1: '教师', 2: '学生', 4: '部门' };
      return map[this.participantType] || '参会人';
    },
    effectiveCampusId() {
      return normalizeDeptId(this.campusId) || '';
    },
    campusDisplayName() {
      return this.campusName || '当前校区';
    },
    canOpenPicker() {
      if (!this.schoolId) return false;
      if (!this.roomId) return false;
      return true;
    },
    fieldTip() {
      if (!this.roomId) {
        return '请先选择会议室，再添加参会人';
      }
      const campusTip = this.effectiveCampusId ? `（${this.campusDisplayName}）` : '（全校）';
      const map = {
        1: `可选；按会议室校区${campusTip}添加教师`,
        2: `可选；按会议室校区${campusTip}选择班级与学生`,
        4: `可选；按会议室校区${campusTip}选择部门节点`
      };
      return map[this.participantType] || map[1];
    },
    orgTreeExpandedKeys() {
      return (this.orgTreeOptions || [])
        .slice(0, 3)
        .map(node => node.value)
        .filter(Boolean);
    },
    pickerDialogTitle() {
      return `添加${this.participantTypeLabel}参会人`;
    },
    filteredDialogOptions() {
      const keyword = (this.dialogSearch || '').trim().toLowerCase();
      if (!keyword) return this.participantOptions;
      return this.participantOptions.filter(item => {
        const name = String(item.ParticipantName || '').toLowerCase();
        const sub = String(item.SubTitle || '').toLowerCase();
        return name.includes(keyword) || sub.includes(keyword);
      });
    },
    confirmDisabled() {
      if (this.participantType === 4) return !this.orgCheckedCount;
      return !this.dialogPickKeys.length;
    },
    selectionSummaryText() {
      const list = this.selections;
      if (!list.length) return '';
      if (list.length === 1) return `已选 1 人`;
      const byType = {};
      list.forEach(p => {
        const label = this.participantTypeText(p.Type);
        byType[label] = (byType[label] || 0) + 1;
      });
      const detail = Object.keys(byType).map(label => `${label} ${byType[label]}`).join(' · ');
      return `已选 ${list.length} 人（${detail}）`;
    },
    showManageButton() {
      return this.selections.length > this.tagInlineMax;
    },
    visibleTagSelections() {
      if (this.selections.length <= this.tagInlineMax) return this.selections;
      return this.selections.slice(0, this.tagPreviewCount);
    },
    hiddenTagCount() {
      if (this.selections.length <= this.tagInlineMax) return 0;
      return this.selections.length - this.tagPreviewCount;
    },
    filteredListSelections() {
      const keyword = (this.listSearch || '').trim().toLowerCase();
      if (!keyword) return this.selections;
      return this.selections.filter(p => {
        const name = String(p.Name || '').toLowerCase();
        const type = this.participantTypeText(p.Type).toLowerCase();
        return name.includes(keyword) || type.includes(keyword);
      });
    }
  },
  watch: {
    schoolId: {
      immediate: true,
      handler() {
        this.resetPickerState();
        this.loadDeptTree();
      }
    },
    campusId(val, oldVal) {
      if (deptIdsEqual(val, oldVal)) return;
      this.participantOptions = [];
      this.teacherLoadedKey = '';
      this.studentClassId = null;
      this.rebuildScopedTrees();
    },
    roomId(val, oldVal) {
      if (String(val || '') === String(oldVal || '')) return;
      this.participantOptions = [];
      this.teacherLoadedKey = '';
      this.studentClassId = null;
      this.rebuildScopedTrees();
    }
  },
  methods: {
    resetPickerState() {
      this.participantOptions = [];
      this.dialogPickKeys = [];
      this.dialogSearch = '';
      this.studentClassId = null;
      this.teacherLoadedKey = '';
      this.orgCheckedCount = 0;
    },
    reset() {
      this.selections = [];
      this.participantType = 1;
      this.resetPickerState();
      this.pickerDialogVisible = false;
      this.listDialogVisible = false;
      this.listSearch = '';
    },
    onParticipantTypeChange() {
      this.resetPickerState();
      if ([2, 4].includes(this.participantType) && !this.schoolDeptNode) {
        this.loadDeptTree();
      }
    },
    openPickerDialog() {
      if (!this.canOpenPicker) {
        if (!this.roomId) {
          this.$message.warning('请先选择会议室');
        }
        return;
      }
      this.pickerDialogVisible = true;
    },
    onPickerDialogOpen() {
      this.dialogPickKeys = [];
      this.dialogSearch = '';
      this.orgCheckedCount = 0;
      this.participantOptions = [];
      if (this.participantType === 1) {
        this.loadTeacherOptions(true);
      } else if ([2, 4].includes(this.participantType) && !this.orgTreeOptions.length && !this.classTreeOptions.length) {
        this.loadDeptTree();
      }
      if (this.participantType === 4) {
        this.$nextTick(() => this.syncOrgTreeChecked());
      }
    },
    onPickerDialogClosed() {
      this.dialogPickKeys = [];
      this.dialogSearch = '';
      this.orgCheckedCount = 0;
    },
    loadDeptTree() {
      if (!this.schoolId || this.deptTreeLoading) return;
      this.deptTreeLoading = true;
      loadSchoolDeptTree(this.schoolId).then(schoolNode => {
        this.deptTreeLoading = false;
        this.schoolDeptNode = schoolNode;
        this.rebuildScopedTrees();
        if (this.participantType === 4 && this.pickerDialogVisible) {
          this.$nextTick(() => this.syncOrgTreeChecked());
        }
      }).catch(() => { this.deptTreeLoading = false; });
    },
    rebuildScopedTrees() {
      if (!this.schoolDeptNode) return;
      const scoped = buildCampusScopedPickerTrees(this.schoolDeptNode, this.effectiveCampusId);
      this.classTreeOptions = scoped.classTreeOptions;
      this.orgTreeOptions = scoped.orgTreeOptions;
    },
    loadTeacherOptions(force) {
      if (!this.schoolId || !this.roomId || this.participantLoading) return;
      const campusFilter = this.effectiveCampusId;
      const deptId = campusFilter > 0 ? campusFilter : this.schoolId;
      const loadKey = `${deptId}:${campusFilter}`;
      if (!force && this.participantOptions.length && this.teacherLoadedKey === loadKey) return;
      this.participantLoading = true;
      fetchTeachingTeachersByDeptId(deptId, campusFilter).then(rows => {
        this.participantLoading = false;
        this.teacherLoadedKey = loadKey;
        this.participantOptions = (rows || []).map(t => ({
          ParticipantType: 1,
          ParticipantId: normalizeOptionalIdString(t.Id),
          ParticipantName: t.TeacherName
        })).filter(t => t.ParticipantId);
      }).catch(() => { this.participantLoading = false; });
    },
    onStudentClassChange(classId) {
      this.dialogPickKeys = [];
      this.dialogSearch = '';
      this.participantOptions = [];
      if (!classId) return;
      this.loadStudentsByClass(classId);
    },
    loadStudentsByClass(classId) {
      this.participantLoading = true;
      getStudentSelectListByClassId(classId).then(res => {
        this.participantLoading = false;
        if (!res.data.success) return;
        const rows = res.data.response || [];
        this.participantOptions = rows.map(s => ({
          ParticipantType: 2,
          ParticipantId: normalizeOptionalIdString(s.Id),
          ParticipantName: s.Name
        })).filter(s => s.ParticipantId);
      }).catch(() => { this.participantLoading = false; });
    },
    selectAllDialogOptions() {
      this.dialogPickKeys = this.filteredDialogOptions.map(p => this.optionKey(p));
    },
    confirmPickerDialog() {
      if (this.participantType === 4) {
        this.confirmOrgPick();
        return;
      }
      this.commitDialogPickKeys();
    },
    filterOrgNode(keyword, data) {
      if (!keyword) return true;
      return String(data.label || '').toLowerCase().includes(String(keyword).trim().toLowerCase());
    },
    filterOrgTree(keyword) {
      const tree = this.$refs.orgTree;
      if (tree) tree.filter(keyword);
    },
    syncOrgTreeChecked() {
      const tree = this.$refs.orgTree;
      if (!tree) return;
      const keys = this.selections
        .filter(p => p.Type === 3 || p.Type === 4)
        .map(p => Number(p.Id))
        .filter(id => id > 0);
      tree.setCheckedKeys(keys);
      this.updateOrgCheckedCount();
    },
    updateOrgCheckedCount() {
      const tree = this.$refs.orgTree;
      this.orgCheckedCount = tree ? tree.getCheckedNodes(false, false).length : 0;
    },
    onOrgTreeCheck(data, state) {
      const tree = this.$refs.orgTree;
      if (!tree || !data) {
        this.updateOrgCheckedCount();
        return;
      }
      const checkedKeys = state.checkedKeys || [];
      const isChecked = checkedKeys.includes(data.value);
      if (isChecked) {
        collectDescendantNodeIds(data).forEach(id => tree.setChecked(id, false, false));
        findAncestorNodeIds(this.orgTreeOptions, data.value).forEach(id => tree.setChecked(id, false, false));
      }
      this.updateOrgCheckedCount();
    },
    confirmOrgPick() {
      const tree = this.$refs.orgTree;
      if (!tree) return;
      const checkedNodes = tree.getCheckedNodes(false, false);
      if (!checkedNodes.length) {
        this.$message.warning('请至少选择一个部门节点');
        return;
      }
      const finalNodes = dedupeOrgSelectionNodes(checkedNodes, this.orgTreeOptions);
      // 批量添加须本地累加后一次 emit，避免 Vue2 同 tick 内 prop 未回写导致只保留最后一项。最后更新：2026-08-13
      let next = (this.selections || []).slice();
      let addedCount = 0;
      finalNodes.forEach(node => {
        const type = resolveOrgParticipantType(node);
        if (this.pushSelection(next, type, node.value, node.label)) {
          addedCount += 1;
        }
      });
      if (addedCount > 0) {
        this.selections = next;
        const mergedHint = finalNodes.length < checkedNodes.length ? '（已合并重复范围）' : '';
        this.$message.success(`已添加 ${addedCount} 个部门${mergedHint}`);
        this.pickerDialogVisible = false;
      } else {
        this.$message.warning('所选部门均已添加');
      }
    },
    optionKey(item) {
      return `${item.ParticipantType}:${item.ParticipantId}`;
    },
    participantOptionLabel(item) {
      const sub = item.SubTitle ? `（${item.SubTitle}）` : '';
      return `${item.ParticipantName || item.ParticipantId}${sub}`;
    },
    participantTypeText(type) {
      const map = { 1: '教师', 2: '学生', 3: '部门', 4: '班级' };
      return map[type] || type;
    },
    /**
     * 向列表追加参会人（不 emit）。批量场景请用此方法累加后再一次性赋值 selections。
     */
    pushSelection(list, type, id, name) {
      const sid = normalizeOptionalIdString(id);
      if (!sid || !list) return false;
      const exists = list.some(p => String(p.Type) === String(type) && String(p.Id) === sid);
      if (exists) return false;
      list.push({
        Type: type,
        Id: sid,
        Name: name || sid
      });
      return true;
    },
    addSelection(type, id, name, silent) {
      const next = (this.selections || []).slice();
      if (!this.pushSelection(next, type, id, name)) {
        if (!silent) this.$message.warning('该参会人已添加');
        return false;
      }
      this.selections = next;
      return true;
    },
    commitDialogPickKeys() {
      const keys = this.dialogPickKeys || [];
      if (!keys.length) return;
      // 批量添加须本地累加后一次 emit，避免 Vue2 同 tick 内 prop 未回写导致只保留最后一项。最后更新：2026-08-13
      let next = (this.selections || []).slice();
      let addedCount = 0;
      keys.forEach(key => {
        const item = this.participantOptions.find(p => this.optionKey(p) === key);
        if (!item) return;
        if (this.pushSelection(next, item.ParticipantType, item.ParticipantId, item.ParticipantName)) {
          addedCount += 1;
        }
      });
      if (addedCount > 0) {
        this.selections = next;
        this.$message.success(`已添加 ${addedCount} 位参会人`);
        this.pickerDialogVisible = false;
      } else {
        this.$message.warning('所选参会人均已添加');
      }
    },
    removeParticipant(index) {
      const next = this.selections.slice();
      next.splice(index, 1);
      this.selections = next;
      if (!next.length) this.listDialogVisible = false;
    },
    getSelectionIndex(item) {
      return this.selections.findIndex(p => String(p.Type) === String(item.Type) && String(p.Id) === String(item.Id));
    },
    clearAllParticipants() {
      if (!this.selections.length) return;
      const clear = () => {
        this.selections = [];
        this.listDialogVisible = false;
        this.listSearch = '';
      };
      if (this.selections.length > 10) {
        this.$confirm('确定清空全部参会人？', '提示', { type: 'warning' }).then(clear).catch(() => {});
        return;
      }
      clear();
    },
    buildPayload() {
      return this.selections.map(p => ({ Type: p.Type, Id: p.Id, Name: p.Name || '' }));
    }
  }
};
</script>

<style scoped>
.participant-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.participant-bar__type {
  width: 96px;
  flex-shrink: 0;
}

.participant-selection {
  margin-top: 8px;
}

.participant-selection__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.participant-selection__summary {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}

.participant-selection__bar-actions {
  flex-shrink: 0;
  white-space: nowrap;
}

.participant-selection__bar-actions .el-button + .el-button {
  margin-left: 0;
}

.participant-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 6px;
}

.participant-tags.is-scroll {
  max-height: 88px;
  overflow-y: auto;
  padding-right: 2px;
}

.participant-tags .el-tag {
  margin: 0;
  max-width: 100%;
}

.participant-tags__more {
  cursor: pointer;
}

.participant-list-dialog__toolbar.toolbar-filter-bar--embed {
  margin-bottom: 10px;
}

.participant-list-dialog__count {
  flex-shrink: 0;
  font-size: 12px;
  color: #94a3b8;
  line-height: 32px;
}

.participant-list-dialog__body {
  max-height: 360px;
  overflow-y: auto;
  border: 1px solid #eef2f7;
  border-radius: 8px;
}

.participant-list-dialog__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid #f1f5f9;
}

.participant-list-dialog__item:last-child {
  border-bottom: none;
}

.participant-list-dialog__name {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: #334155;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.participant-list-dialog__type {
  display: inline-block;
  margin-right: 6px;
  padding: 0 6px;
  border-radius: 4px;
  background: #eff6ff;
  color: #3b82f6;
  font-size: 11px;
  line-height: 18px;
}

.participant-list-dialog__remove {
  flex-shrink: 0;
  color: #ef4444;
}

.participant-list-dialog__empty {
  padding: 48px 16px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
}

.field-tip {
  margin-top: 6px;
  color: #909399;
  font-size: 12px;
}

.picker-dialog__block + .picker-dialog__block {
  margin-top: 16px;
}

.picker-dialog__label {
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
}

.picker-dialog__label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.picker-dialog__label-row--compact {
  min-height: 0;
  margin-bottom: 4px;
}

.picker-dialog__actions .el-button + .el-button {
  margin-left: 4px;
}

.picker-dialog__search {
  margin-bottom: 8px;
}

.picker-dialog__list {
  min-height: 200px;
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fafbfc;
  padding: 8px 12px;
}

.picker-dialog__checks {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.picker-dialog__checks >>> .el-checkbox {
  display: flex;
  align-items: center;
  margin-right: 0;
  padding: 6px 8px;
  border-radius: 6px;
  transition: background 0.15s;
}

.picker-dialog__scope-tip {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #eff6ff;
  border: 1px solid #dbeafe;
  color: #1e40af;
  font-size: 13px;
}

.picker-dialog__scope-tip.is-muted {
  background: #f8fafc;
  border-color: #e2e8f0;
  color: #64748b;
}

.picker-dialog__scope-tip i {
  font-size: 15px;
}

.picker-dialog__checks >>> .el-checkbox:hover {
  background: #eff6ff;
}

.picker-dialog__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  color: #94a3b8;
  font-size: 13px;
}

.picker-dialog__hint {
  margin: 10px 0 0;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.5;
}

.picker-dialog__list--tree {
  padding: 8px 10px;
}

.picker-dialog__list--tree >>> .el-tree-node__content {
  height: 32px;
  border-radius: 6px;
}

.picker-dialog__list--tree >>> .el-tree-node__content:hover {
  background: #eff6ff;
}

.org-tree-node {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
}

.picker-dialog__footer-tip {
  float: left;
  line-height: 32px;
  font-size: 13px;
  color: #64748b;
}
</style>

<style>
.mr-participant-dialog .el-dialog__body {
  padding-top: 12px;
  padding-bottom: 8px;
}

.mr-participant-list-dialog .el-dialog__body {
  padding-top: 12px;
  padding-bottom: 16px;
}
</style>
